/**
 * ArcepCard — couverture fixe (fibre / THD / HD) de la commune.
 *
 * UX spec :
 *   - Barre empilée 4 segments : fibre / THD / HD / inéligible.
 *   - Pastilles compteurs : % fibre mis en avant, puis THD, HD, inéligibles.
 *   - Footer : nom de la commune + date d'observation ARCEP.
 *
 * Granularité : ARCEP publie par commune, pas par adresse. On rend donc le
 * chiffre au niveau commune (pas du quartier) — ce que l'utilisateur
 * comprend intuitivement, la fibre étant déployée commune par commune.
 */

import type { FicheDoc } from "../../lib/fiches";
import { useArcep, type ArcepData } from "../../lib/env/arcep";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function ArcepCard({ fiche }: Props) {
  const state = useArcep(fiche.id, fiche.geocode);
  const subtitle = state.status === "ready" ? summarise(state.data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Cpu size={18} />}
      title="Couverture fibre & internet"
      subtitle={subtitle}
      state={state}
      footer={
        state.status === "ready" ? (
          <>
            Source : ARCEP · « Ma connexion Internet ». Mesure à l'échelle de
            la commune ({state.data.commune || fiche.geocode?.city || "—"}),
            mise à jour {state.data.observationDate || "mensuelle"}.
          </>
        ) : (
          <>Source : ARCEP · « Ma connexion Internet », maille communale.</>
        )
      }
    >
      {state.status === "ready" && <ArcepBody data={state.data} />}
    </EnvCardShell>
  );
}

// ─── Summary ─────────────────────────────────────────────────────────────

function summarise(d: ArcepData): string {
  const fiber = formatPct(d.fiber_pct);
  if (d.fiber_pct >= 95) return `${fiber} éligibles fibre · commune intégralement couverte`;
  if (d.fiber_pct >= 70) return `${fiber} éligibles fibre · déploiement avancé`;
  if (d.fiber_pct >= 30) return `${fiber} éligibles fibre · déploiement en cours`;
  if (d.fiber_pct > 0) return `${fiber} éligibles fibre · déploiement initial`;
  return `Fibre non déployée · ${formatPct(d.thd_pct + d.hd_pct)} en HD/THD`;
}

// ─── Body ────────────────────────────────────────────────────────────────

function ArcepBody({ data }: { data: ArcepData }) {
  // The four bands : visually stacked as a single bar. Colours reflect the
  // ARCEP wording — green for fiber/THD (good), amber for HD (adequate),
  // rose for inéligible (bad).
  const bands = [
    { key: "fiber",   label: "Fibre ≥ 1 Gbps",   pct: data.fiber_pct,   color: "bg-emerald-500" },
    { key: "thd",     label: "THD 30 Mbps – 1 Gbps", pct: data.thd_pct, color: "bg-lime-400" },
    { key: "hd",      label: "HD 3 – 30 Mbps",    pct: data.hd_pct,    color: "bg-amber-300" },
    { key: "nothing", label: "Inéligibles HD",    pct: data.nothing_pct, color: "bg-rose-400" },
  ];

  return (
    <div className="space-y-4">
      {/* Stacked bar */}
      <div>
        <div className="flex h-3 w-full rounded-full overflow-hidden bg-ink-100">
          {bands.map((b) =>
            b.pct > 0 ? (
              <div
                key={b.key}
                className={b.color}
                style={{ width: `${b.pct}%` }}
                title={`${b.label} — ${formatPct(b.pct)}`}
              />
            ) : null,
          )}
        </div>

        {/* Legend / values — 2×2 on narrow, 4×1 in full-width mode so the
            card doesn't look empty when it spans both columns of the grid. */}
        <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5">
          {bands.map((b) => (
            <li key={b.key} className="flex items-center gap-2 text-[12.5px]">
              <span className={`w-2.5 h-2.5 rounded-sm ${b.color} shrink-0`} />
              <span className="text-ink-700 truncate flex-1">{b.label}</span>
              <span className="tabular text-ink-900 font-medium">
                {formatPct(b.pct)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Total locaux */}
      <div className="text-[11.5px] text-ink-500 border-t border-ink-100 pt-3">
        Basé sur {data.total_locaux.toLocaleString("fr-FR")} locaux recensés
        dans la commune.
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatPct(p: number): string {
  if (p >= 100) return "100 %";
  if (p <= 0) return "0 %";
  if (p >= 10) return `${Math.round(p)} %`;
  return `${p.toFixed(1).replace(".", ",")} %`;
}
